<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12 mt-5">
        <div>
          <TableTitle
            title="Release Notes"
            pageTitle="Release Notes"
            icon="fas fa-clipboard-list"
            :showAction="true"
            actionLabel="Create Release Note"
            @action="openCreateModal"
            :disabled="!canCreate('website')"
          />
          <div class="table_style">
            <CommonTable2
              :tableObj="tableConfig"
              @edit="editReleaseNote"
              @delete="deleteReleaseNote"
              @view="viewReleaseNote"
              @statusFilter="handleStatusFilter"
              @resetFilters="resetFilters"
              @page-changed="handlePageChange"
              permissionKey="website"
            />
          </div>

          <!-- pagination -->
          <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-4">
            <!-- Rows per page -->
            <div class="d-flex align-items-center gap-2">
              <span class="text-muted">Rows per page:</span>
              <select
                class="form-select form-select-sm rows-select"
                v-model="tablePagination.per_page"
                @change="changeRows"
              >
                <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
                  {{ n }}
                </option>
              </select>
            </div>

            <!-- Pagination Buttons -->
            <div class="pagination-mini">
              <!-- Previous -->
              <button
                class="page-btn prev_btn"
                :disabled="!tablePagination.has_prev"
                @click="handlePageChange(tablePagination.current_page - 1)"
              >
                «
              </button>
              <!-- Numbered Pages -->
              <button
                v-for="(p, index) in visiblePages"
                :key="index"
                class="page-btn"
                :class="{
                  'page-active': p === tablePagination.current_page,
                  'disabled-dots': p === '...',
                }"
                :disabled="p === '...'"
                @click="p !== '...' && handlePageChange(p)"
              >
                {{ p }}
              </button>

              <!-- Next -->
              <button
                class="page-btn next_btn"
                :disabled="!tablePagination.has_next"
                @click="handlePageChange(tablePagination.current_page + 1)"
              >
                »
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Release Note Create/Edit Modal -->
    <ReleaseNoteModal
      :show="showModal"
      :releaseNote="selectedReleaseNote"
      :readonly="isViewMode"
      @close="closeModal"
      @saved="handleSaved"
    />
  </div>
</template>

<script setup lang="ts">
import {
  ref,
  reactive,
  computed,
  onMounted,
  watch,
  defineAsyncComponent,
} from "vue";
import { useRouter } from "vue-router";
import apiService from "@/services/apiService";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import TableTitle from "@/components/commen/table/TableTitle.vue";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

const ReleaseNoteModal = defineAsyncComponent(
  () => import("@/components/pms/admin/ReleaseNoteModal.vue")
);

const router = useRouter();
const loading = ref(false);
const rows = ref([]);
const selectedReleaseNote = ref(null);
const showModal = ref(false);
const isViewMode = ref(false);

interface Filters {
  status: string;
}
// Filters
const filters = reactive<Filters>({
  status: "",
});

// pagination
const currentPage = ref(1);
const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;

  // If total pages small → show all
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  let pages: any[] = [];

  // Always show first page
  pages.push(1);

  // Left side "..."
  if (current > 4) {
    pages.push("...");
  }

  // Middle window (current -1, current, current +1)
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);

  for (let i = windowStart; i <= windowEnd; i++) {
    pages.push(i);
  }

  // Right side "..."
  if (current < total - 3) {
    pages.push("...");
  }

  // Always show last page
  pages.push(total);

  return pages;
});

const changeRows = () => {
  currentPage.value = 1;
  fetchReleaseNotes();
};

watch(
  () => tablePagination.value,
  (newVal) => {
    tableConfig.value.pagination = newVal;
  },
  { deep: true }
);

const resetFilters = () => {
  filters.status = "";
  currentPage.value = 1;
};

// FETCH RELEASE NOTES
const fetchReleaseNotes = async () => {
  try {
    loading.value = true;
    const params: Record<string, any> = {
      page: currentPage.value,
      limit: tablePagination.value.per_page,
      // ...filters, // If backend supports filtering
    };

    const response = await apiService.getReleaseNotes(params);
    const data = response.data || [];
    rows.value = data.map((item: any) => ({
      ...item,
      // Map for CommonTable2 if needed or keep raw
      description: item.description || '', // Keep raw HTML for editing
      description_preview: item.description ? item.description.replace(/<[^>]*>?/gm, '').substring(0, 100) + (item.description.length > 100 ? '...' : '') : '',
      version: item.release_note_version || "-",
      date: item.created_at ? new Date(item.created_at).toLocaleDateString() : "-",
    }));

    tablePagination.value = {
      current_page: response.pagination?.current_page || 1,
      per_page: params.limit,
      total: response.pagination?.total || 0,
      total_pages: response.pagination?.total_pages || 1,
      has_next: response.pagination?.has_next || false,
      has_prev: response.pagination?.has_prev || false,
    };
  } catch (error) {
    console.error("Error fetching release notes:", error);
    rows.value = [];
  } finally {
    loading.value = false;
  }
};

const openCreateModal = () => {
  selectedReleaseNote.value = null;
  isViewMode.value = false;
  showModal.value = true;
};

const editReleaseNote = (row_or_id: any) => {
  let note = null;
  if (typeof row_or_id === 'string') {
      note = rows.value.find((r: any) => r._id === row_or_id);
  } else {
      note = row_or_id; // It's the row object
  }

  if (note) {
    selectedReleaseNote.value = note;
    isViewMode.value = false;
    showModal.value = true;
  }
};

const viewReleaseNote = (note: any) => {
    selectedReleaseNote.value = note;
    isViewMode.value = true;
    showModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedReleaseNote.value = null;
};

const handleSaved = () => {
  closeModal();
  fetchReleaseNotes();
};

const handleStatusFilter = (val: string) => {
  filters.status = val.toLowerCase();
  currentPage.value = 1;
};

const deleteReleaseNote = async (id: string) => {
  if (confirm("Delete this Release Note?")) {
    try {
      await apiService.deleteReleaseNote(id);
      // Small delay to ensure backend consistency
      setTimeout(() => {
        fetchReleaseNotes();
      }, 500); 
    } catch (error) {
      console.error("Error deleting note:", error);
    }
  }
};

watch(
  [filters],
  () => {
    fetchReleaseNotes();
  },
  { deep: true }
);

// Load on mount
onMounted(() => {
  fetchReleaseNotes();
});

// table config
const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true, width: "200px" },
    { name: "Description", field: "description_preview", width: "auto", isHtml: false }, 
    { name: "Major Version", field: "major_version", width: "120px" },
    { name: "Version", field: "release_note_version", width: "100px" },
    { name: "Date", field: "date", width: "150px" },
  ],
  data: rows,
  actions: [true],
  loading: loading,
  pagination: tablePagination,
  showCategoryFilter: false,
  showStatusFilter: false, 
  showResetFilter: false,
  table_width: "100%",
});

const handlePageChange = (page: any) => {
  currentPage.value = page;
  fetchReleaseNotes();
};
</script>

<style scoped>
/* Reuse styles from BlogsList or define new ones */
.table_style {
  overflow-x: auto;
}

.rows-select {
  width: 82px;
  padding: 8px 17px;
  font-size: 0.85rem;
  background-color: transparent;
}

/* Mini Pagination */
.pagination-mini {
  display: flex;
  align-items: center;
  border-radius: 6px;
  overflow: hidden;
}

.page-btn {
  border: none;
  background: transparent;
  font-size: 1rem;
  padding: 7px 12px;
  cursor: pointer;
  color: #6c757d;
  border-radius: 0px;
  transition: 0.2s;
  border: 1px solid #dee2e6;
  outline: none;
}
.page-btn:first-child {
  border-top-left-radius: 6px;
  border-bottom-left-radius: 6px;
}
.page-btn:last-child {
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
}
.page-btn:hover {
  background: #e9ecef;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-active {
  background: #21c1d6;
  color: #fff;
  padding: 7px 12px;
  font-weight: 600;
  min-width: 28px;
  text-align: center;
  display: inline-block;
  border: 1px solid #21c1d6;
}
.prev_btn,
.next_btn {
  color: #21c1d6;
}
.page-active:hover{
   background: #21c1d6;
}
</style>